<template>
  <div id="TimeEssay">
    <b-container fluid class="essay-area">
      <b-row class="py-5 px-sm-0 px-md-0 px-lg-0">
        <div class="editor-wrapper">
          <div class="main-writing-card">
            <div class="essay-heading">
              <h2 class="essay-heading-title mb-1">Write your essay</h2>
              <p class="essay-heading-subtitle mb-0">
                Start your response below. Begin with contextualization or your thesis.
              </p>
            </div>

            <div class="editor-meta-row" aria-live="polite">
              <span
                v-if="saveStatus === 'saving'"
                class="autosave-status autosave-status--saving"
              >Saving…</span>
              <span
                v-else-if="saveStatus === 'saved'"
                class="autosave-status autosave-status--saved"
              >&#10003;&nbsp;Saved just now</span>
              <span
                v-else-if="saveStatus === 'all-saved'"
                class="autosave-status autosave-status--saved"
              >&#10003;&nbsp;All changes saved</span>
              <span
                v-else-if="saveStatus === 'offline'"
                class="autosave-status autosave-status--offline"
              >&#9679;&nbsp;Offline — changes saved locally</span>
            </div>

            <b-textarea
              ref="textarea"
              class="no-scroll-bar textbox-border w-100 main-essay-textarea"
              rows="9"
              max-rows="18"
              no-resize
              v-model="essay"
              placeholder="Start your essay here... You might begin by setting historical context or stating your argument."
            ></b-textarea>
          </div>
        </div>
      </b-row>
      <b-row align-h="end" class="px-5 pt-3 pb-3">
        <b-button
          class="finish-button border-0 mb-2 fontist purple-button"
          @click="next(buttonprops[1])"
        >
          Next: Label Essay Sections
        </b-button>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { pushRoute } from "@/router/navigation";
import store from "@/store/writing";
import { useAutosaveDraft } from "@/composables/useAutosaveDraft";

export default {
  props: ["text", "promptId"],
  emits: ["TimedSubmit"],
  setup(props, context) {
    const textarea = ref(null);
    const essay = ref(props.text || "");

    const { saveStatus, loadDraft, onSubmitted } = useAutosaveDraft(
      essay,
      props.promptId,
      { moduleVersion: props.moduleVersion }
    );

    onMounted(async () => {
      await loadDraft();
    });

    const buttonprops = [
      {
        content: "<   Go Back to Modules",
        route: "001",
        disabled: false
      },
      { content: "Continue   >", route: "005", disabled: false }
    ];

    const next = async obj => {
      context.emit("TimedSubmit", essay.value);
      store.dispatch("setModuleVersion", props.moduleVersion);
      await onSubmitted();
      if (obj.params) {
        pushRoute(obj.route, obj.params);
      } else {
        pushRoute(obj.route);
      }
    };

    return {
      next,
      buttonprops,
      essay,
      textarea,
      saveStatus
    };
  }
};
</script>

<style>
#TimeEssay {
  margin: 0;
  padding: 0;
}

.editor-wrapper {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  padding: 0 24px !important;
}

.main-writing-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f9fafb;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
}

.essay-heading {
  margin-bottom: 8px;
}

.essay-heading-title {
  color: var(--text-normal, #18191f);
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.essay-heading-subtitle {
  color: var(--text-normal-2, #4b5563);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.35;
}

.editor-meta-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 0;
  margin-bottom: 4px;
  padding-right: 2px;
}

.autosave-status {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.2;
  transition: opacity 0.4s ease;
  white-space: nowrap;
}

.autosave-status--saving {
  color: #9ca3af; /* cool gray — neutral in-progress */
}

.autosave-status--saved {
  color: #6b9e80; /* muted sage-green — calm success */
}

.autosave-status--offline {
  color: #b08040; /* muted amber — noticeable but not alarming */
}

.main-essay-textarea {
  border: 1px solid #edf0f5;
  border-radius: 12px;
  background: #ffffff;
  padding: 16px !important;
  font-size: 17px;
  line-height: 1.45;
  min-height: 260px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.main-essay-textarea:focus {
  border-color: rgba(140, 48, 245, 0.25);
  box-shadow: 0 0 0 2px rgba(140, 48, 245, 0.1);
}

@media (max-width: 992px) {
  .editor-wrapper {
    padding: 0 16px !important;
  }

  .essay-heading-title {
    font-size: 24px;
  }

  .main-writing-card {
    padding: 14px;
  }

  .main-essay-textarea {
    min-height: 280px;
  }
}
</style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
